/**
 * Visual editor.
 *
 * The real site in an iframe, with every editable element outlined. Click one
 * and its editor opens beside it — no hunting through forms for the field that
 * corresponds to the thing you were looking at.
 *
 * Coordination is postMessage across origins (site and admin are different
 * hosts). The overlay inside the iframe is deliberately dumb: it reports which
 * id was clicked and nothing else. All knowledge of what fields mean lives here
 * and in the shared registry, so adding a field never means touching the
 * overlay script.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  EDITABLE_FIELDS,
  FIELD_TYPES,
  buildPatch,
  fieldsByGroup,
  getField,
  readPath,
} from '@agenz/shared/editable';
import { api } from '../lib/api.js';
import {
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  SaveState,
  Select,
  Spinner,
  Textarea,
} from '../components/ui.jsx';

const PAGES = [
  { path: '/', label: 'Home' },
  { path: '/work', label: 'Work' },
  { path: '/services', label: 'Services' },
  { path: '/about', label: 'About' },
  { path: '/process', label: 'Process' },
  { path: '/contact', label: 'Contact' },
];

const VIEWPORTS = [
  { id: 'desktop', label: 'Desktop', width: '100%' },
  { id: 'tablet', label: 'Tablet', width: '820px' },
  { id: 'mobile', label: 'Mobile', width: '414px' },
];

/** Reorderable list of plain strings. */
function ListEditor({ value = [], onChange, itemLabel = 'Item', multiline }) {
  const items = Array.isArray(value) ? value : [];

  const move = (from, to) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="border-3 border-ink bg-paper p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted">
              {itemLabel} {index + 1}
            </span>
            <div className="flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={index === 0}
                onClick={() => move(index, index - 1)}
                aria-label="Move up"
              >
                ↑
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={index === items.length - 1}
                onClick={() => move(index, index + 1)}
                aria-label="Move down"
              >
                ↓
              </Button>
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={() => onChange(items.filter((_, i) => i !== index))}
                aria-label="Remove"
              >
                ×
              </Button>
            </div>
          </div>
          {multiline ? (
            <Textarea
              rows={2}
              value={item}
              onChange={(e) => onChange(items.map((v, i) => (i === index ? e.target.value : v)))}
            />
          ) : (
            <Input
              value={item}
              onChange={(e) => onChange(items.map((v, i) => (i === index ? e.target.value : v)))}
            />
          )}
        </div>
      ))}
      <Button type="button" variant="secondary" size="sm" onClick={() => onChange([...items, ''])}>
        + Add {itemLabel.toLowerCase()}
      </Button>
    </div>
  );
}

/** Reorderable list of {number,title,body} steps. */
function StepsEditor({ value = [], onChange }) {
  const steps = Array.isArray(value) ? value : [];
  const set = (i, k, v) => onChange(steps.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const move = (from, to) => {
    if (to < 0 || to >= steps.length) return;
    const next = [...steps];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {steps.map((step, index) => (
        <div key={index} className="border-3 border-ink bg-paper p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted">
              Step {index + 1}
            </span>
            <div className="flex gap-1">
              <Button type="button" variant="ghost" size="sm" disabled={index === 0} onClick={() => move(index, index - 1)}>↑</Button>
              <Button type="button" variant="ghost" size="sm" disabled={index === steps.length - 1} onClick={() => move(index, index + 1)}>↓</Button>
              <Button type="button" variant="danger" size="sm" onClick={() => onChange(steps.filter((_, i) => i !== index))}>×</Button>
            </div>
          </div>
          <div className="flex gap-2">
            <Input className="w-16" value={step.number ?? ''} placeholder="01" onChange={(e) => set(index, 'number', e.target.value)} />
            <Input value={step.title ?? ''} placeholder="Title" onChange={(e) => set(index, 'title', e.target.value)} />
          </div>
          <Textarea className="mt-2" rows={2} value={step.body ?? ''} placeholder="Description" onChange={(e) => set(index, 'body', e.target.value)} />
        </div>
      ))}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => onChange([...steps, { number: String(steps.length + 1).padStart(2, '0'), title: '', body: '' }])}
      >
        + Add step
      </Button>
    </div>
  );
}

export default function VisualEditor() {
  const frame = useRef(null);
  const [token, setToken] = useState(null);
  const [settings, setSettings] = useState(null);
  const [page, setPage] = useState('/');
  const [viewport, setViewport] = useState('desktop');
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);

  const siteOrigin = settings?.siteUrl ? new URL(settings.siteUrl).origin : null;

  // ── Load token + settings ────────────────────────────────────────────────
  const load = useCallback(() => {
    setError(null);
    Promise.all([
      api.post('/api/admin/preview-token'),
      api.get('/api/admin/settings'),
      api.get('/api/site/settings'),
    ])
      .then(([tok, adminSettings, publicSettings]) => {
        setToken(tok.token);
        setSettings({
          ...(adminSettings.settings ?? {}),
          // The public payload carries the resolved values, which is what the
          // page is actually rendering — editing should start from that, not
          // from the sparse stored overrides.
          resolved: publicSettings,
          siteUrl: publicSettings.siteUrl ?? window.location.origin.replace('admin.', ''),
        });
      })
      .catch((err) => setError(err.message));
  }, []);

  useEffect(load, [load]);

  // ── Talk to the iframe ───────────────────────────────────────────────────
  const send = useCallback(
    (message) => {
      if (!frame.current?.contentWindow || !siteOrigin) return;
      frame.current.contentWindow.postMessage({ source: 'agenz-admin', ...message }, siteOrigin);
    },
    [siteOrigin],
  );

  useEffect(() => {
    function onMessage(event) {
      if (siteOrigin && event.origin !== siteOrigin) return;
      const data = event.data;
      if (!data || data.source !== 'agenz-edit') return;

      if (data.type === 'loaded') {
        send({ type: 'handshake' });
        return;
      }
      if (data.type === 'select') {
        setSelectedId(data.id);
        return;
      }
      if (data.type === 'navigate' && data.href?.startsWith('/')) {
        // Links are inert inside the iframe; treat a click as a request to
        // switch which page we are editing.
        const known = PAGES.find((p) => p.path === data.href);
        if (known) setPage(known.path);
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [siteOrigin, send]);

  // ── Selection → draft ────────────────────────────────────────────────────
  const field = selectedId ? getField(selectedId) : null;

  useEffect(() => {
    if (!field || !settings) {
      setDraft(null);
      return;
    }
    const current = readPath(settings.resolved ?? {}, field.path);
    setDraft(structuredClone(current ?? (field.type === FIELD_TYPES.LIST || field.type === FIELD_TYPES.STEPS ? [] : '')));
  }, [selectedId, field, settings]);

  const groups = useMemo(() => fieldsByGroup(page), [page]);

  async function save() {
    if (!field) return;
    setState('saving');
    setError(null);
    try {
      const patch = buildPatch(field, draft, settings.resolved);
      await api.patch('/api/admin/settings', patch);
      const refreshed = await api.get('/api/site/settings');
      setSettings((prev) => ({ ...prev, resolved: refreshed }));
      setState('saved');
      // Give the site a moment to drop its render cache before reloading.
      setTimeout(() => send({ type: 'reload' }), 900);
      setTimeout(() => setState('idle'), 2400);
    } catch (err) {
      setState('error');
      setError(err.message);
    }
  }

  if (error && !settings) return <ErrorNote error={error} onRetry={load} />;
  if (!settings || !token) return <Spinner label="Opening the site…" />;

  const frameSrc = `${settings.siteUrl}${page}?agenzEdit=${encodeURIComponent(token)}`;
  const frameWidth = VIEWPORTS.find((v) => v.id === viewport)?.width ?? '100%';

  return (
    <div className="flex h-[calc(100vh-5rem)] flex-col">
      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl">Visual editor</h1>
        <Select value={page} onChange={(e) => setPage(e.target.value)} className="w-40">
          {PAGES.map((p) => (
            <option key={p.path} value={p.path}>{p.label}</option>
          ))}
        </Select>
        <div className="flex gap-1">
          {VIEWPORTS.map((v) => (
            <Button
              key={v.id}
              size="sm"
              variant={viewport === v.id ? 'primary' : 'secondary'}
              onClick={() => setViewport(v.id)}
            >
              {v.label}
            </Button>
          ))}
        </div>
        <span className="ml-auto text-xs text-muted">
          Click any outlined element in the page to edit it
        </span>
      </div>

      <div className="flex min-h-0 flex-1 gap-4">
        {/* Live site */}
        <div className="border-brutal min-h-0 flex-1 overflow-hidden bg-line/40">
          <iframe
            ref={frame}
            key={`${page}-${token}`}
            src={frameSrc}
            title="Site preview"
            className="mx-auto block h-full border-0 bg-paper transition-[width] duration-300"
            style={{ width: frameWidth }}
            onLoad={() => send({ type: 'handshake' })}
          />
        </div>

        {/* Editor panel */}
        <div className="w-96 shrink-0 overflow-y-auto">
          <ErrorNote error={error} />

          {field ? (
            <Card className="p-5">
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted">
                    {field.group}
                  </p>
                  <h2 className="text-lg">{field.label}</h2>
                </div>
                <SaveState state={state} />
              </div>

              {field.type === FIELD_TYPES.TEXT && (
                <Input value={draft ?? ''} onChange={(e) => setDraft(e.target.value)} autoFocus />
              )}
              {field.type === FIELD_TYPES.TEXTAREA && (
                <Textarea rows={6} value={draft ?? ''} onChange={(e) => setDraft(e.target.value)} autoFocus />
              )}
              {field.type === FIELD_TYPES.NUMBER && (
                <Input type="number" value={draft ?? 0} onChange={(e) => setDraft(Number(e.target.value))} />
              )}
              {field.type === FIELD_TYPES.COLOR && (
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={draft ?? '#000000'}
                    onChange={(e) => setDraft(e.target.value.toUpperCase())}
                    className="size-12 shrink-0 cursor-pointer border-3 border-ink"
                  />
                  <Input value={draft ?? ''} onChange={(e) => setDraft(e.target.value.toUpperCase())} className="font-mono text-xs" />
                </div>
              )}
              {field.type === FIELD_TYPES.LIST && (
                <ListEditor value={draft} onChange={setDraft} itemLabel={field.itemLabel} multiline={field.multiline} />
              )}
              {field.type === FIELD_TYPES.STEPS && <StepsEditor value={draft} onChange={setDraft} />}

              <div className="mt-5 flex gap-2 border-t-3 border-ink pt-4">
                <Button onClick={save} disabled={state === 'saving'}>Save</Button>
                <Button variant="ghost" onClick={() => setSelectedId(null)}>Close</Button>
              </div>
            </Card>
          ) : (
            <Card className="border-dashed p-5">
              <h2 className="text-lg">Nothing selected</h2>
              <p className="mt-2 text-sm text-muted">
                Click an outlined element in the page, or pick one below.
              </p>
            </Card>
          )}

          {/* Outline — everything editable on this page */}
          <div className="mt-4 space-y-3">
            {groups.map(({ group, fields }) => (
              <Card key={group} className="p-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted">{group}</p>
                <div className="mt-2 space-y-1">
                  {fields.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setSelectedId(item.id);
                        send({ type: 'highlight', id: item.id });
                      }}
                      className={`block w-full px-2 py-1 text-left text-sm transition-colors ${
                        selectedId === item.id ? 'bg-ink text-paper' : 'hover:bg-acid'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
